
import Vue, { PropType } from 'vue';
import { get } from 'lodash';
import Api from '@/core/api';
import { Unsubscriber } from '@/core/event-bus';
import { SubtitleTrack, PlayerState } from '@/core/player-state';
import IconButton from '@/common/icon-button/IconButton.vue';
import IconButtonWithDropdown from '@/common/icon-button-with-dropdown/IconButtonWithDropdown.vue';
import VideoPlayerMenu from '@/common/video-player-menu/VideoPlayerMenu.vue';
import { MenuItem } from '@/common/video-player-menu/menu-item';
import { Icon } from '@/common/icon/Icon.vue';

interface LanguageConfig {
    // Maps the subtitle track's label to a name
    subtitleTrackNames?: Record<string, string>;

    // Only the tracks which label is contained in this array will be shown.
    // If not present, all will be visible
    visibleSubtitleTrackLabels?: string[];
}

function trackToMenuItem(
    subtitleTrack: SubtitleTrack | null,
    subtitleTrackNameDictionary: Record<string, string>
): MenuItem | null {
    if (!subtitleTrack) {
        return null;
    }

    return {
        id: subtitleTrack.id,
        value: subtitleTrackNameDictionary[subtitleTrack.label] || subtitleTrack.label
    };
}

const SUBTITLES_OFF_MENU_ITEM: MenuItem = {
    id: 'off',
    value: 'Off'
};

export default Vue.extend({
    components: {
        IconButton,
        IconButtonWithDropdown,
        VideoPlayerMenu
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        config: {
            type: Object as PropType<LanguageConfig>,
            default: () => ({} as LanguageConfig)
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        },
        isLeft: {
            type: Boolean as PropType<boolean>,
            default: false
        }
    },

    data() {
        return {
            subtitleTracks: [] as SubtitleTrack[],
            selectedSubtitleTrack: null as SubtitleTrack | null,
            subtitlesEnabled: false as boolean,
            unsubscribers: [] as Unsubscriber[]
        };
    },

    computed: {
        icon(): Icon {
            return this.subtitlesEnabled ? Icon.captionsOn : Icon.captionsOff;
        },

        subtitleTrackNameDictionary(): Record<string, string> {
            return this.config?.subtitleTrackNames ?? {};
        },

        visibleSubtitleTrackLabels(): Set<string> {
            return new Set(this.config?.visibleSubtitleTrackLabels ?? []);
        },

        isSubtitleTrackVisibilityRestricted(): boolean {
            return !!this.config?.visibleSubtitleTrackLabels;
        },

        subtitleMenuItems(): MenuItem[] {
            const menuItems: MenuItem[] = [];

            if (!this.subtitleTracks || !this.subtitleTracks.length) {
                return menuItems;
            }

            menuItems.push(SUBTITLES_OFF_MENU_ITEM);

            for (const track of this.subtitleTracks) {
                const menuItem = trackToMenuItem(track, this.subtitleTrackNameDictionary);
                if (menuItem) {
                    menuItems.push(menuItem);
                }
            }

            return menuItems;
        },

        selectedSubtitleMenuItem(): MenuItem | null {
            if (!this.subtitlesEnabled) {
                return SUBTITLES_OFF_MENU_ITEM;
            }

            return trackToMenuItem(this.selectedSubtitleTrack, this.subtitleTrackNameDictionary);
        },

        hasSubtitleTracks(): boolean {
            return this.subtitleTracks && !!this.subtitleTracks.length;
        },

        subtitleTrackCount(): number {
            return (this.subtitleTracks || []).length;
        },

        tooltip(): string {
            const defaultTooltip = get(this.labels, 'tooltip', '') as string;

            if (this.subtitleTrackCount === 1) {
                if (this.subtitlesEnabled) {
                    return get(this.labels, 'tooltip_disable', defaultTooltip) as string;
                }
                return get(this.labels, 'tooltip_enable', defaultTooltip) as string;
            }

            return defaultTooltip;
        }
    },

    mounted() {
        const { unsubscriber, state } = this.api.onStateChange(
            (partial: Partial<PlayerState>) => {
                this.updateState(partial);
            }
        );

        this.updateState(state);

        this.unsubscribers.push(unsubscriber);
    },

    beforeDestroy() {
        for (const unsubscriber of this.unsubscribers) {
            if (typeof unsubscriber === 'function') {
                unsubscriber();
            }
        }
    },

    methods: {
        updateState(state: Partial<PlayerState>) {
            this.selectedSubtitleTrack = get(state, 'subtitleTrackActive', this.selectedSubtitleTrack);

            this.subtitlesEnabled = get(state, 'subtitlesEnabled', this.subtitlesEnabled);

            this.subtitleTracks = get(state, 'subtitleTracks', this.subtitleTracks)
                .filter(track => !this.isSubtitleTrackVisibilityRestricted || this.visibleSubtitleTrackLabels.has(track.label));
        },

        onSubtitleSelected(item: MenuItem) {
            if (item.id === SUBTITLES_OFF_MENU_ITEM.id) {
                this.api.disableSubtitles();
                return;
            }

            const track = this.subtitleTracks.find(t => t.id === item.id);
            if (track) {
                this.api.selectSubtitleTrack(track);
            }
        },

        onSingleTrackButtonClicked() {
            if (this.subtitlesEnabled) {
                this.api.disableSubtitles();
            } else {
                this.api.selectSubtitleTrack(this.subtitleTracks[0]);
            }
        }
    }
});
